import { Navigate } from 'react-router-dom';
import useFeaturePermissions from '../../hooks/useFeaturePermissions';
import useParentRegistrationStatus from '../../hooks/useParentRegistrationStatus';
import useModuleAccess from '../../hooks/useModuleAccess';
import MickeyLoader from '../common/MickeyLoader';

// Fun animated loading component - Mickey Head with Disneyland background
const AnimatedLoadingScreen = ({ message }) => (
  <MickeyLoader message={message || "Memuatkan..."} />
);

// Map route paths to feature IDs
const ROUTE_FEATURE_MAP = {
  '/activities': 'activities',
  '/nutrition': 'nutrition',
  '/daily-logs': 'daily_logs',
  '/development': 'development',
  '/nutritional-status': 'nutritional_status',
  '/children': 'children',
  '/classes': 'children',
  '/reports': 'reports',
  '/billing': 'invoices',
};

// Routes that require child registration for parents
const PARENT_RESTRICTED_ROUTES = [
  '/activities',
  '/daily-logs',
  '/children',
  '/takwim',
  '/classes',
  '/nutrition',
  '/development',
  '/nutritional-status'
];

export default function ProtectedRoute({ children, user, path }) {
  const { hasAccess, loading } = useFeaturePermissions(user?.role);
  const { status: registrationStatus, loading: regLoading } = useParentRegistrationStatus(user);
  const { isPathAllowed, loading: moduleLoading } = useModuleAccess(user?.role);
  
  // Kawalan Module Global: sekat semua role kecuali superadmin jika module OFF
  if (user?.role !== 'superadmin') {
    if (moduleLoading) {
      return <AnimatedLoadingScreen message="Memeriksa akses..." />;
    }
    if (path && !isPathAllowed(path)) {
      return <Navigate to="/" replace />;
    }
  }
  
  // Admin, Superadmin, and Branch Manager bypass all restrictions
  if (user?.role === 'admin' || user?.role === 'superadmin' || user?.role === 'branch_manager') {
    return children;
  }

  // Guru also bypasses parent registration check
  if (user?.role === 'guru') {
    // Get the feature ID for this route
    const featureId = ROUTE_FEATURE_MAP[path];
    
    // If no feature mapping, allow access
    if (!featureId) {
      return children;
    }
    
    // Show loading while checking permissions
    if (loading) {
      return <AnimatedLoadingScreen message="Memeriksa akses..." />;
    }
    
    // Check if user has access to this feature
    if (!hasAccess(featureId)) {
      return <Navigate to="/" replace />;
    }
    
    return children;
  }
  
  // For parents (ibu_bapa), check if they have registered children for restricted routes
  if (user?.role === 'ibu_bapa' && PARENT_RESTRICTED_ROUTES.includes(path)) {
    // Show loading while checking registration status
    if (regLoading) {
      return <AnimatedLoadingScreen message="Memeriksa status pendaftaran..." />;
    }
    
    // Block access if no registered children
    if (registrationStatus && !registrationStatus.can_access_features) {
      return <Navigate to="/" replace />;
    }
  }
  
  // Get the feature ID for this route
  const featureId = ROUTE_FEATURE_MAP[path];
  
  // If no feature mapping, allow access
  if (!featureId) {
    return children;
  }
  
  // Show loading while checking permissions
  if (loading) {
    return <AnimatedLoadingScreen message="Memeriksa akses..." />;
  }
  
  // Check if user has access to this feature - redirect to dashboard if not
  if (!hasAccess(featureId)) {
    return <Navigate to="/" replace />;
  }
  
  return children;
}
